import { flow, getEnv, types } from 'mobx-state-tree';

import { createMapWithTransform } from 'utils/store/createMap';
import AiModuleModel, { createAiModuleModel } from './AiModuleModel';
import { ItemLoadingStateEnum, ListLoadingStateEnum } from './LoadingStateEnums';
import { StoreEnv } from './StoreEnv';

const AiModulesStore = types
  .model('AiModulesStore', {
    itemLoadingState: types.maybe(ItemLoadingStateEnum),
    listLoadingState: types.maybe(ListLoadingStateEnum),
    aiModules: types.maybe(types.map(AiModuleModel))
  })
  .actions((self) => {
    const getAiModules = flow(function* () {
      const { client, applicationStore } = getEnv<StoreEnv>(self);

      try {
        self.listLoadingState = 'loading';
        const result: any = yield client.getAiModules();

        const modules = result?.ai_modules || result;
        self.aiModules = createMapWithTransform(modules, createAiModuleModel);
        self.listLoadingState = undefined;
      } catch (error: any) {
        if (process.env.NODE_ENV !== 'production') {
          // tslint:disable-next-line
          console.error('AiModulesStore | getAiModules', error, error.body);
        }

        if (client.isNotFound(error)) {
          self.listLoadingState = 'not_found';
          return undefined;
        }

        if (applicationStore.handleAppError(error)) {
          return undefined;
        }

        self.listLoadingState = 'load_error';
        return undefined;
      }
    });

    const getAiModule = flow(function* (aiModuleId: number) {
      const { client, applicationStore } = getEnv<StoreEnv>(self);

      try {
        self.itemLoadingState = 'loading';
        const result: any = yield client.getAiModule(aiModuleId);

        if (result) {
          const module = createAiModuleModel(result);
          if (!self.aiModules) {
            self.aiModules = types.map(AiModuleModel).create({});
          }
          self.aiModules.put(module);
        }

        self.itemLoadingState = undefined;
      } catch (error: any) {
        if (process.env.NODE_ENV !== 'production') {
          // tslint:disable-next-line
          console.error('AiModulesStore | getAiModule', error, error.body);
        }

        if (client.isNotFound(error)) {
          self.itemLoadingState = 'not_found';
          return undefined;
        }

        if (applicationStore.handleAppError(error)) {
          return undefined;
        }

        self.itemLoadingState = 'load_error';
        return undefined;
      }
    });

    return {
      getAiModules,
      getAiModule
    };
  })
  .views((self) => {
    return {
      get isListLoading(): boolean {
        return self.listLoadingState === 'loading';
      },
      get isListError(): boolean {
        return (
          self.listLoadingState === 'load_error' ||
          self.listLoadingState === 'access_denied' ||
          self.listLoadingState === 'feature_disabled'
        );
      },
      get isItemLoading(): boolean {
        return self.itemLoadingState === 'loading';
      },
      get isItemLoadError(): boolean {
        return (
          self.itemLoadingState === 'load_error' ||
          self.itemLoadingState === 'access_denied' ||
          self.itemLoadingState === 'feature_disabled'
        );
      },
      get aiModuleList() {
        if (!self.aiModules) {
          return [];
        }
        return Array.from(self.aiModules.values());
      },
      aiModuleListForStrategy(fireStrategy?: string) {
        const all = self.aiModules
          ? Array.from(self.aiModules.values())
          : [];
        if (!fireStrategy) {
          return all;
        }
        return all.filter(
          (m) =>
            m.strategy_settings.length === 0 ||
            m.strategy_settings.some((s) => s.strategy_type === fireStrategy)
        );
      }
    };
  });

export type AiModulesStoreType = typeof AiModulesStore.Type;
export default AiModulesStore;
